import React, { useState, useEffect, useContext } from "react";
import { api } from "../../api";
import Ctx from "../../contexts/AppContext";
import { crd, bdg, sp, rad, type, icBox, listRow } from "../../constants/styles";
import Ic from "../common/Ic";
import { formatPrice } from "../../utils/plans";

const ALL_FEATURES = [
  "basic_dashboard", "payments", "maintenance", "whatsapp", "leases",
  "service_persons", "agreements", "commercial", "reports", "csv_export",
  "analytics", "automation", "multi_user", "priority_support"
];
const FEATURE_LABELS = {
  basic_dashboard: "Dashboard", payments: "Rent Payments", maintenance: "Maintenance",
  whatsapp: "WhatsApp", leases: "Leases", service_persons: "Service Persons",
  agreements: "Agreements", commercial: "Commercial", reports: "Reports",
  csv_export: "CSV Export", analytics: "Analytics", automation: "Automation",
  multi_user: "Multi-User", priority_support: "Priority Support"
};

export default function AdminPricing() {
  const { h, go, flash } = useContext(Ctx);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // plan id or "new"
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  // Free-trial config — toggle controls whether brand-new landlord
  // signups get N days of a paid plan before downgrading to Free.
  // Stored as a singleton row in `settings` (org_id='global').
  const [trial, setTrial] = useState({ enabled: false, duration_days: 30, plan_id: 'pro' });
  const [trialSaving, setTrialSaving] = useState(false);
  const [trialSavedFlash, setTrialSavedFlash] = useState(false);

  const load = async () => {
    const [plansRes, trialRes] = await Promise.all([
      api.adminGetPlans(),
      api.adminGetTrialConfig(),
    ]);
    if (plansRes.success) setPlans(plansRes.plans);
    if (trialRes.success && trialRes.config) setTrial(trialRes.config);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const saveTrial = async (next) => {
    setTrialSaving(true);
    const res = await api.adminSetTrialConfig(next);
    setTrialSaving(false);
    if (res.success) {
      setTrial(res.config || next);
      setTrialSavedFlash(true);
      flash?.("Trial config saved");
      setTimeout(() => setTrialSavedFlash(false), 1800);
    } else {
      flash?.(res.message || "Failed to save trial config", "error");
    }
  };

  const startEdit = (plan) => {
    setEditing(plan.id);
    setForm({ ...plan, features: plan.features || [] });
  };

  const startNew = () => {
    setEditing("new");
    setForm({ id: "", name: "", price: 0, billing_cycle: "monthly", max_properties: 3, max_units: 5, max_tenants: 5, features: ["basic_dashboard", "payments"], is_active: 1, sort_order: plans.length });
  };

  const toggleFeature = (f) => {
    const features = form.features || [];
    setForm(prev => ({
      ...prev,
      features: features.includes(f) ? features.filter(x => x !== f) : [...features, f]
    }));
  };

  const handleSave = async () => {
    if (!form.name) { flash("Name required", "error"); return; }
    setSaving(true);
    let res;
    if (editing === "new") {
      if (!form.id) { flash("ID required", "error"); setSaving(false); return; }
      res = await api.adminCreatePlan(form);
    } else {
      res = await api.adminUpdatePlan(editing, form);
    }
    if (res.success) {
      flash(editing === "new" ? "Plan created!" : "Plan updated!");
      setEditing(null);
      await load();
    } else {
      flash(res.message || "Failed to save", "error");
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Delete plan "${id}"?`)) return;
    const res = await api.adminDeletePlan(id);
    if (res.success) {
      flash(res.message || "Plan deleted");
      await load();
    } else {
      flash(res.message || "Failed to delete", "error");
    }
  };

  const fieldS = {
    width: "100%", padding: `${sp.sm}px ${sp.md}px`, borderRadius: rad.sm,
    border: `1px solid ${h.bd}`, background: h.ib, color: h.tx,
    ...type.body, outline: "none", boxSizing: "border-box",
  };

  if (loading) {
    return <div style={{ paddingTop: 40, textAlign: "center" }}>
      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke={h.ac} strokeWidth="2" style={{ animation: "spin 1s linear infinite" }}><path d="M21 12a9 9 0 11-6.219-8.56" /></svg>
    </div>;
  }

  // Edit/Create form
  if (editing) {
    return <div style={{ paddingTop: sp.lg }} className="animate-fadeIn">
      <button onClick={() => setEditing(null)} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", ...type.sm, marginBottom: sp.md, display: "flex", alignItems: "center", gap: sp.xs }}>
        <Ic n="arrow-left" s={14} c={h.ac} /> Back to Plans
      </button>
      <h2 style={{ ...type.h2, color: h.tx, margin: `0 0 ${sp.lg}px` }}>
        {editing === "new" ? "Create Plan" : `Edit: ${form.name}`}
      </h2>

      <div style={{ ...crd(h) }}>
        {editing === "new" && <div style={{ marginBottom: sp.lg }}>
          <label style={{ ...type.xs, color: h.ts, display: "block", marginBottom: sp.xs }}>Plan ID (lowercase, no spaces)</label>
          <input value={form.id || ""} onChange={e => setForm(f => ({ ...f, id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') }))} style={fieldS} placeholder="e.g. starter" />
        </div>}

        <div style={{ marginBottom: sp.lg }}>
          <label style={{ ...type.xs, color: h.ts, display: "block", marginBottom: sp.xs }}>Plan Name</label>
          <input value={form.name || ""} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} style={fieldS} placeholder="e.g. Starter" />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: sp.sm, marginBottom: sp.lg }}>
          <div>
            <label style={{ ...type.xs, color: h.ts, display: "block", marginBottom: sp.xs }}>Price (paise)</label>
            <input type="number" value={form.price || 0} onChange={e => setForm(f => ({ ...f, price: parseInt(e.target.value) || 0 }))} style={fieldS} />
            <div style={{ ...type.xs, color: h.tt, marginTop: sp.xs }}>= {formatPrice(form.price)}/mo</div>
          </div>
          <div>
            <label style={{ ...type.xs, color: h.ts, display: "block", marginBottom: sp.xs }}>Sort Order</label>
            <input type="number" value={form.sort_order || 0} onChange={e => setForm(f => ({ ...f, sort_order: parseInt(e.target.value) || 0 }))} style={fieldS} />
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: sp.sm, marginBottom: sp.lg }}>
          <div>
            <label style={{ ...type.xs, color: h.ts, display: "block", marginBottom: sp.xs }}>Max Properties</label>
            <input type="number" value={form.max_properties || 0} onChange={e => setForm(f => ({ ...f, max_properties: parseInt(e.target.value) || 0 }))} style={fieldS} />
          </div>
          <div>
            <label style={{ ...type.xs, color: h.ts, display: "block", marginBottom: sp.xs }}>Max Units</label>
            <input type="number" value={form.max_units || 0} onChange={e => setForm(f => ({ ...f, max_units: parseInt(e.target.value) || 0 }))} style={fieldS} />
          </div>
          <div>
            <label style={{ ...type.xs, color: h.ts, display: "block", marginBottom: sp.xs }}>Max Tenants</label>
            <input type="number" value={form.max_tenants || 0} onChange={e => setForm(f => ({ ...f, max_tenants: parseInt(e.target.value) || 0 }))} style={fieldS} />
          </div>
        </div>

        <div style={{ marginBottom: sp.lg }}>
          <label style={{ ...type.xs, color: h.ts, display: "block", marginBottom: sp.sm }}>Features</label>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: sp.xs }}>
            {ALL_FEATURES.map(f => {
              const on = (form.features || []).includes(f);
              return <div key={f} onClick={() => toggleFeature(f)} style={{
                display: "flex", alignItems: "center", gap: sp.sm, padding: `${sp.xs}px ${sp.sm}px`,
                borderRadius: rad.sm, border: `1px solid ${on ? h.ac : h.bd}`,
                background: on ? `${h.ac}08` : "transparent", cursor: "pointer"
              }}>
                <div style={{ width: 16, height: 16, borderRadius: 4, border: `2px solid ${on ? h.ac : h.bd}`, background: on ? h.ac : "transparent", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {on && <span style={{ color: "#fff", fontSize: 10, fontWeight: 700 }}>{"\u2713"}</span>}
                </div>
                <span style={{ ...type.xs, color: h.tx }}>{FEATURE_LABELS[f] || f}</span>
              </div>;
            })}
          </div>
        </div>

        <div style={{ display: "flex", alignItems: "center", gap: sp.md, marginBottom: sp.lg }}>
          <label style={{ ...type.xs, color: h.ts }}>Active</label>
          <div onClick={() => setForm(f => ({ ...f, is_active: f.is_active ? 0 : 1 }))} style={{
            width: 44, height: 24, borderRadius: 12, background: form.is_active ? h.gn : h.bd,
            cursor: "pointer", position: "relative", transition: "background 0.2s"
          }}>
            <div style={{ width: 20, height: 20, borderRadius: 10, background: "#fff", position: "absolute", top: 2, left: form.is_active ? 22 : 2, transition: "left 0.2s", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }} />
          </div>
        </div>

        <button onClick={handleSave} disabled={saving}
          style={{
            width: "100%", padding: sp.md, borderRadius: rad.md, border: "none",
            background: h.ac, color: "#fff", ...type.sm, fontWeight: 600,
            cursor: "pointer", opacity: saving ? 0.6 : 1
          }}
        >{saving ? "Saving..." : editing === "new" ? "Create Plan" : "Save Changes"}</button>
      </div>
    </div>;
  }

  // Plans list view
  return <div style={{ paddingTop: sp.lg }} className="animate-fadeIn">
    <button onClick={() => go("dash")} style={{ background: "none", border: "none", color: h.ac, cursor: "pointer", ...type.sm, marginBottom: sp.md, display: "flex", alignItems: "center", gap: sp.xs }}>
      <Ic n="arrow-left" s={14} c={h.ac} /> Dashboard
    </button>

    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: sp.lg }}>
      <h2 style={{ ...type.h2, color: h.tx, margin: 0 }}>Pricing & Plans</h2>
      <button onClick={startNew} style={{
        padding: `${sp.xs}px ${sp.md}px`, borderRadius: rad.sm, border: "none",
        background: h.ac, color: "#fff", ...type.xs, fontWeight: 600, cursor: "pointer",
        display: "flex", alignItems: "center", gap: sp.xs
      }}>
        <Ic n="plus" s={12} c="#fff" /> Add Plan
      </button>
    </div>

    {/* Free-Trial Toggle — applies to NEW landlord signups only.
        Existing orgs are unaffected. After expiry the existing
        plan-expiry checker downgrades them to Free automatically. */}
    <div style={{ ...crd(h), marginBottom: sp.md, position: "relative" }}>
      <div style={{ display: "flex", alignItems: "center", gap: sp.md, marginBottom: sp.sm }}>
        <div style={icBox(h.gn, 40)}>
          <span style={{ fontSize: 18 }}>🎁</span>
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ ...type.sm, color: h.tx, fontWeight: 700 }}>Free Trial for New Signups</div>
          <div style={{ ...type.xs, color: h.sc, marginTop: 2 }}>
            Every new landlord gets full plan features for N days, then auto-downgrades to Free.
          </div>
        </div>
        <label style={{ position: "relative", display: "inline-block", width: 44, height: 24, cursor: trialSaving ? "wait" : "pointer", flexShrink: 0 }}>
          <input
            type="checkbox"
            checked={!!trial.enabled}
            disabled={trialSaving}
            onChange={(e) => saveTrial({ ...trial, enabled: e.target.checked })}
            style={{ opacity: 0, width: 0, height: 0 }}
          />
          <span style={{
            position: "absolute", inset: 0,
            background: trial.enabled ? h.gn : "#ccc",
            borderRadius: 24,
            transition: "0.2s",
          }} />
          <span style={{
            position: "absolute", top: 2, left: trial.enabled ? 22 : 2,
            width: 20, height: 20, background: "#fff", borderRadius: "50%",
            transition: "0.2s",
            boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
          }} />
        </label>
      </div>

      {trial.enabled && (
        <div style={{ display: "flex", gap: sp.sm, marginTop: sp.sm, paddingTop: sp.sm, borderTop: `1px dashed ${h.bd}`, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div style={{ flex: "1 1 110px" }}>
            <label style={{ ...type.xs, color: h.sc, fontWeight: 600, display: "block", marginBottom: 4 }}>Duration (days)</label>
            <input
              type="number"
              min={1}
              max={365}
              value={trial.duration_days}
              onChange={(e) => setTrial({ ...trial, duration_days: parseInt(e.target.value, 10) || 30 })}
              onBlur={() => saveTrial(trial)}
              style={{ width: "100%", padding: `${sp.sm}px ${sp.md}px`, borderRadius: rad.sm, border: `1px solid ${h.bd}`, background: h.ib, color: h.tx, fontSize: 13, boxSizing: "border-box" }}
            />
          </div>
          <div style={{ flex: "1 1 140px" }}>
            <label style={{ ...type.xs, color: h.sc, fontWeight: 600, display: "block", marginBottom: 4 }}>Trial Plan</label>
            <select
              value={trial.plan_id}
              onChange={(e) => saveTrial({ ...trial, plan_id: e.target.value })}
              disabled={trialSaving}
              style={{ width: "100%", padding: `${sp.sm}px ${sp.md}px`, borderRadius: rad.sm, border: `1px solid ${h.bd}`, background: h.ib, color: h.tx, fontSize: 13 }}
            >
              {plans
                .filter(p => p.is_active && p.id !== 'free')
                .map(p => <option key={p.id} value={p.id}>{p.name} (₹{formatPrice(p.price)}/mo)</option>)}
              {plans.filter(p => p.is_active && p.id !== 'free').length === 0 && (
                <option value="">— no paid plans configured —</option>
              )}
            </select>
          </div>
          {trialSavedFlash && (
            <div style={{ ...type.xs, color: h.gn, fontWeight: 600, padding: `${sp.sm}px 0` }}>✓ Saved</div>
          )}
        </div>
      )}
    </div>

    {/* Plans Grid */}
    <div style={{ display: "flex", flexDirection: "column", gap: sp.sm }}>
      {plans.map(p => <div key={p.id} style={{ ...crd(h), position: "relative" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div style={{ display: "flex", alignItems: "center", gap: sp.md }}>
            <div style={icBox(p.price > 0 ? h.gn : h.wn, 40)}>
              <Ic n={p.price > 0 ? "zap" : "shield"} s={18} c={p.price > 0 ? h.gn : h.wn} />
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: sp.sm }}>
                <span style={{ ...type.h3, color: h.tx }}>{p.name}</span>
                <span style={bdg(p.is_active ? `${h.gn}20` : `${h.rd}20`, p.is_active ? h.gn : h.rd)}>
                  {p.is_active ? "Active" : "Inactive"}
                </span>
              </div>
              <div style={{ ...type.xs, color: h.ts, marginTop: 2 }}>
                ID: {p.id} {"\u00B7"} {p.max_properties >= 999999 ? "Unlimited" : p.max_properties} props {"\u00B7"} {(p.features || []).length} features
              </div>
            </div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ ...type.h2, color: p.price > 0 ? h.gn : h.wn }}>{formatPrice(p.price)}</div>
            <div style={{ ...type.xs, color: h.tt }}>/month</div>
          </div>
        </div>

        {/* Features chips */}
        <div style={{ display: "flex", flexWrap: "wrap", gap: sp.xs, marginTop: sp.md }}>
          {(p.features || []).map(f => <span key={f} style={{
            ...bdg(`${h.ac}10`, h.ac), fontSize: 9
          }}>{FEATURE_LABELS[f] || f}</span>)}
        </div>

        {/* Actions */}
        <div style={{ display: "flex", gap: sp.sm, marginTop: sp.md, paddingTop: sp.sm, borderTop: `1px solid ${h.bd}` }}>
          <button onClick={() => startEdit(p)} style={{
            flex: 1, padding: sp.sm, borderRadius: rad.sm, border: `1px solid ${h.ac}`,
            background: "transparent", color: h.ac, ...type.xs, fontWeight: 600, cursor: "pointer"
          }}>Edit</button>
          {p.id !== "free" && <button onClick={() => handleDelete(p.id)} style={{
            padding: `${sp.sm}px ${sp.md}px`, borderRadius: rad.sm, border: `1px solid ${h.rd}30`,
            background: "transparent", color: h.rd, ...type.xs, fontWeight: 600, cursor: "pointer"
          }}>Delete</button>}
        </div>
      </div>)}
    </div>
  </div>;
}
